import { Comparison, Metric, Polarity, ValueUnit } from 'modules/arena/reports/model/types';

// Formatação pt-BR dos números do relatório. Dinheiro chega em CENTAVOS (inteiros) e só é
// dividido para exibição. null é sempre "Indisponível" — nunca vira zero.

export const UNAVAILABLE = 'Indisponível';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const INT = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const ONE_DECIMAL = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 });

/** Valores negativos levam o sinal de menos explícito (−), nunca são escondidos. */
export function formatMoney(cents: number | null): string {
  if (cents === null) return UNAVAILABLE;
  const formatted = BRL.format(Math.abs(cents) / 100);
  return cents < 0 ? `− ${formatted}` : formatted;
}

export function formatCount(value: number | null): string {
  return value === null ? UNAVAILABLE : INT.format(value);
}

export function formatPercent(value: number | null): string {
  return value === null ? UNAVAILABLE : `${ONE_DECIMAL.format(value)}%`;
}

export function formatHours(value: number | null): string {
  return value === null ? UNAVAILABLE : `${ONE_DECIMAL.format(value)} h`;
}

/** Nota na escala informada pela integração: "4,8/5". */
export function formatRating(value: number | null, scale = 5): string {
  return value === null ? 'Ainda sem avaliações' : `${ONE_DECIMAL.format(value)}/${scale}`;
}

export function formatValue(value: number | null, unit: ValueUnit): string {
  switch (unit) {
    case 'money':
      return formatMoney(value);
    case 'percent':
      return formatPercent(value);
    case 'hours':
      return formatHours(value);
    case 'rating':
      return formatRating(value);
    default:
      return formatCount(value);
  }
}

export function formatMetric(metric: Metric): string {
  return formatValue(metric.value, metric.unit);
}

export type ComparisonTone = 'good' | 'bad' | 'neutral' | 'none';

export interface ComparisonView {
  text: string;
  tone: ComparisonTone;
  direction: 'up' | 'down' | 'flat' | null;
}

/**
 * Como mostrar a variação contra o período anterior. Sem base (anterior zero/indisponível)
 * não há percentual. A cor depende do sentido do indicador: mais cancelamentos é ruim.
 */
export function describeComparison(comparison: Comparison | null | undefined, polarity: Polarity): ComparisonView | null {
  if (!comparison) return null;
  if (comparison.deltaPct === null || comparison.previousValue === null || comparison.previousValue === 0) {
    return { text: 'Sem base para comparação', tone: 'none', direction: null };
  }
  const delta = comparison.deltaPct;
  if (Math.abs(delta) < 0.05) return { text: 'Estável (0%)', tone: 'neutral', direction: 'flat' };

  const direction = delta > 0 ? 'up' : 'down';
  const tone: ComparisonTone = polarity === 'neutral' ? 'neutral' : (polarity === 'up') === (delta > 0) ? 'good' : 'bad';
  return { text: `${delta > 0 ? '+' : '−'}${ONE_DECIMAL.format(Math.abs(delta))}%`, tone, direction };
}
